// AvailabilitiesTab – Create and view all provider availabilities.
// Clicking a time slot opens an inline detail view (with update/delete)
// instead of navigating to a separate page.
import React, { useMemo, useState, useEffect } from "react";
import {
  createAvailability,
  getFreeStartTimes,
  getAvailabilityInfoByStart,
  getSlotsByDate,
  updateAvailability,
  deleteAvailability,
  getAvailabilityById,
} from "../APIs/availability";

// --- Minimal zone list (add more as you like) ---
const ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Tokyo",
];

// Guess user zone
const defaultZone = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
})();

// --- Helpers ---
const pad2 = (n) => String(n).padStart(2, "0");

// Derive ISO8601 offset like "-04:00" for a given zone at the selected local wall-time.
function getZoneOffsetString(dateStr, timeStr, zoneId) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const approxUtc = new Date(Date.UTC(y, m - 1, d, hh, mm, 0, 0));

  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: zoneId,
    hour12: false,
    timeZoneName: "shortOffset",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  const parts = fmt.formatToParts(approxUtc);
  const tzPart = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+0";
  const m1 = tzPart.match(/([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!m1) return "+00:00";
  const sign = m1[1] === "-" ? "-" : "+";
  const H = pad2(Number(m1[2]));
  const M = pad2(Number(m1[3] || "0"));
  return `${sign}${H}:${M}`;
}

function toZonedDateTimeString(dateStr, timeStr, zoneId) {
  const offset = getZoneOffsetString(dateStr, timeStr, zoneId);
  return `${dateStr}T${timeStr}:00${offset}[${zoneId}]`;
}

function humanTime(zdtString, zone) {
  const [iso] = zdtString.split("[");
  const d = new Date(iso);
  const effectiveZone = zone || extractZoneFromZdt(zdtString);
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: effectiveZone,
  });
}

// minutes between start and end using the chosen zone (single-day form)
function diffMinutes(dateStr, startTimeStr, endTimeStr, zoneId) {
  const s = toZonedDateTimeString(dateStr, startTimeStr, zoneId).split("[")[0];
  const e = toZonedDateTimeString(dateStr, endTimeStr, zoneId).split("[")[0];
  const ms = new Date(e) - new Date(s);
  return Math.round(ms / 60000);
}

// --- Detail-view helpers (adapted from AvailabilityInfo.js) ---
function fmtDate(dateStrOrZdt) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStrOrZdt)) {
    const [y, m, d] = dateStrOrZdt.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return dt.toLocaleDateString([], {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  const iso = String(dateStrOrZdt).split("[")[0];
  const dt = new Date(iso);
  return dt.toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function extractZoneFromZdt(zdtString) {
  const m = String(zdtString).match(/\[([^\]]+)\]$/);
  return m ? m[1] : defaultZone;
}

function toInputsFromZdt(zdtString) {
  const iso = String(zdtString).split("[")[0];
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function sameInstant(a, b) {
  const ta = new Date(String(a).split("[")[0]).getTime();
  const tb = new Date(String(b).split("[")[0]).getTime();
  return ta === tb;
}

const subTabs = ["create availability", "view availabilities"];

export default function AvailabilitiesTab({ providerId }) {
  const [activeTab, setActiveTab] = useState(subTabs[0]);

  // ---- Create form state ----
  const [type, setType] = useState("BLOCK"); // 'BLOCK' | 'SINGLE'
  const [date, setDate] = useState(() => {
    const z = defaultZone;
    const now = new Date();
    const yyyy = now.toLocaleString("en-CA", { timeZone: z, year: "numeric" });
    const mm = now.toLocaleString("en-CA", { timeZone: z, month: "2-digit" });
    const dd = now.toLocaleString("en-CA", { timeZone: z, day: "2-digit" });
    return `${yyyy}-${mm}-${dd}`;
  });
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("12:00");
  const [zone, setZone] = useState(defaultZone);
  const [duration, setDuration] = useState(30); // minutes, only for BLOCK
  const [telehealth, setTelehealth] = useState(false);
  const [appointmentType, setAppointmentType] = useState("NEW_PATIENT");
  const [submitting, setSubmitting] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);

  // ---- View tab state ----
  const [viewDate, setViewDate] = useState(date);
  const [viewZone, setViewZone] = useState(zone);
  const [viewTelehealth, setViewTelehealth] = useState(""); // "", "true", "false"
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [startTimes, setStartTimes] = useState([]);

  // ---- Detail view state ----
  const [selectedZdt, setSelectedZdt] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailInfo, setDetailInfo] = useState(null);
  const [slotEndOverride, setSlotEndOverride] = useState(null);
  const [slotStartOverride, setSlotStartOverride] = useState(null);

  // ---- Edit / action state ----
  const [editing, setEditing] = useState(false);
  const [updDate, setUpdDate] = useState("");
  const [updStart, setUpdStart] = useState("");
  const [updEnd, setUpdEnd] = useState("");
  const [updZone, setUpdZone] = useState(defaultZone);
  const [updTele, setUpdTele] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detailMsg, setDetailMsg] = useState("");

  // Validate before submit (client hints; server is authoritative)
  const createErrors = useMemo(() => {
    const errs = [];
    if (!date) errs.push("Date is required.");
    if (!startTime || !endTime) errs.push("Start and End time are required.");
    if (startTime && endTime && startTime >= endTime)
      errs.push("Start must be before End.");

    // duration rules (no grid restriction)
    const total = diffMinutes(date, startTime, endTime, zone);

    if (type === "SINGLE") {
      if (total < 15 || total > 60) {
        errs.push("SINGLE availability must be between 15 and 60 minutes.");
      }
    } else {
      const dur = Number(duration);
      if (!Number.isFinite(dur) || dur < 15 || dur > 60) {
        errs.push("BLOCK duration must be between 15 and 60 minutes.");
      }
      const rem = total % dur;
      if (rem > 0 && rem < 15) {
        errs.push(
          "BLOCK window must be a multiple of duration or leave a final slot ≥ 15 minutes.",
        );
      }
    }

    // Local 'now' guard using offset
    try {
      const now = new Date();
      const isoWithOffset = toZonedDateTimeString(date, startTime, zone).split(
        "[",
      )[0];
      const startAsInstant = new Date(isoWithOffset);
      if (startAsInstant <= now) {
        errs.push("Start time must be in the future.");
      }
    } catch {
      /* ignore */
    }

    return errs;
  }, [date, startTime, endTime, type, duration, zone]);

  const gotoViewTabAndRefresh = async (newDate, newZone, tele) => {
    setViewDate(newDate);
    setViewZone(newZone);
    setViewTelehealth(tele === true ? "true" : tele === false ? "false" : "");
    setActiveTab("view availabilities");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateMsg(null);
    if (createErrors.length) return;

    const body = {
      type,
      startDateTime: toZonedDateTimeString(date, startTime, zone),
      endDateTime: toZonedDateTimeString(date, endTime, zone),
      telehealth: appointmentType === "TELEHEALTH" ? true : telehealth,
      appointmentType,
    };
    if (type === "BLOCK") body.durationMinutes = Number(duration);

    try {
      setSubmitting(true);
      await createAvailability(Number(providerId), body);
      await gotoViewTabAndRefresh(date, zone, "");
    } catch (err) {
      setCreateMsg(err?.message || "Failed to create availability");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchStartTimes = async () => {
    setLoadingTimes(true);
    setStartTimes([]);
    try {
      const teleParam =
        viewTelehealth === "" ? undefined : viewTelehealth === "true";
      const data = await getFreeStartTimes(Number(providerId), {
        date: viewDate,
        zone: viewZone,
        telehealth: teleParam,
      });
      setStartTimes(data || []);
    } catch (err) {
      setStartTimes([]);
      console.error(err);
    } finally {
      setLoadingTimes(false);
    }
  };

  // ---- Detail view handlers ----
  const fetchDetailInfo = async (zdtString) => {
    setDetailLoading(true);
    setDetailError("");
    try {
      const data = await getAvailabilityInfoByStart(providerId, {
        start: zdtString,
        zone: viewZone,
      });
      setDetailInfo(data);

      // Resolve slot-level start/end time
      try {
        const { date: slotDate } = toInputsFromZdt(zdtString);
        const slots = await getSlotsByDate(Number(providerId), {
          date: slotDate,
          zone: viewZone,
        });
        const match = (slots || []).find((s) =>
          sameInstant(s.slotStart, zdtString),
        );
        setSlotStartOverride(match?.slotStart || null);
        setSlotEndOverride(match?.slotEnd || null);
      } catch {
        setSlotStartOverride(null);
        setSlotEndOverride(null);
      }

      // Prefill edit form fields
      const z = extractZoneFromZdt(data.startDateTime) || defaultZone;
      const { date: d, time: t } = toInputsFromZdt(data.startDateTime);
      const { time: endT } = toInputsFromZdt(data.endDateTime);
      setUpdZone(z);
      setUpdDate(d);
      setUpdStart(t);
      setUpdEnd(endT);
      setUpdTele(Boolean(data.telehealth));
    } catch (e) {
      setDetailError(e?.message || "Failed to load availability info");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectSlot = (zdt) => {
    setSelectedZdt(zdt);
    setEditing(false);
    setDetailMsg("");
    fetchDetailInfo(zdt);
  };

  const handleBackToGrid = () => {
    setSelectedZdt(null);
    setDetailLoading(false);
    setDetailError("");
    setDetailInfo(null);
    setSlotEndOverride(null);
    setSlotStartOverride(null);
    setEditing(false);
    setUpdDate("");
    setUpdStart("");
    setUpdEnd("");
    setUpdZone(defaultZone);
    setUpdTele(undefined);
    setSaving(false);
    setDeleting(false);
    setDetailMsg("");
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!detailInfo) return;
    setDetailMsg("");
    if (!updDate || !updStart || !updEnd) {
      setDetailMsg("Please provide date, start, and end.");
      return;
    }
    if (updStart >= updEnd) {
      setDetailMsg("Start must be before End.");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        startDateTime: toZonedDateTimeString(updDate, updStart, updZone),
        endDateTime: toZonedDateTimeString(updDate, updEnd, updZone),
        telehealth: updTele,
      };
      await updateAvailability(
        Number(providerId),
        Number(detailInfo.availabilityId),
        payload,
      );
      const fresh = await getAvailabilityById(
        providerId,
        detailInfo.availabilityId,
        { zone: viewZone },
      );
      setDetailInfo(fresh);
      setEditing(false);
      setDetailMsg("Availability updated.");
    } catch (e) {
      setDetailMsg(e?.message || "Failed to update availability");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detailInfo) return;
    const ok = window.confirm(
      "Delete this availability? This action cannot be undone.",
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteAvailability(
        Number(providerId),
        Number(detailInfo.availabilityId),
      );
      handleBackToGrid();
      fetchStartTimes();
    } catch (e) {
      setDetailMsg(e?.message || "Failed to delete availability");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (activeTab === "view availabilities") {
      fetchStartTimes();
    } else {
      setSelectedZdt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, providerId, viewDate, viewZone, viewTelehealth]);

  const renderDetailView = () => (
    <div className="max-w-3xl p-4 space-y-4">
      <button
        onClick={handleBackToGrid}
        className="mb-4 text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 flex items-center gap-1"
      >
        ← Back to time slots
      </button>

      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Availability Details</h3>
        <div className="text-xs text-gray-500">{viewZone}</div>
      </div>

      {detailLoading && <p className="text-gray-600">Loading…</p>}
      {detailError && !detailLoading && (
        <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {detailError}
        </div>
      )}

      {detailInfo && !detailLoading && !detailError && (
        <>
          <div className="p-4 border rounded bg-white space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase text-gray-500">Date</div>
                <div className="text-base font-medium">
                  {fmtDate(detailInfo.date)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Type</div>
                <div className="text-base font-medium">{detailInfo.type}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Start time
                </div>
                <div className="text-base font-medium">
                  {humanTime(
                    slotStartOverride ||
                      selectedZdt ||
                      detailInfo.startDateTime,
                    viewZone,
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">End time</div>
                <div className="text-base font-medium">
                  {humanTime(
                    slotEndOverride || detailInfo.endDateTime,
                    viewZone,
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Duration</div>
                <div className="text-base font-medium">
                  {detailInfo.durationMinutes} minutes
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Telehealth
                </div>
                <div className="text-base font-medium">
                  {detailInfo.telehealth ? "Yes" : "No"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Status</div>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                    detailInfo.status === "FREE"
                      ? "bg-green-100 text-green-700"
                      : detailInfo.status === "PAST"
                        ? "bg-gray-100 text-gray-700"
                        : detailInfo.status === "BOOKED"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {detailInfo.status}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setEditing((v) => !v)}
              className="px-4 py-2 rounded bg-yellow-500 text-white hover:bg-yellow-600"
            >
              {editing ? "Cancel Edit" : "Update"}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className={`px-4 py-2 rounded text-white ${deleting ? "bg-red-300" : "bg-red-600 hover:bg-red-700"}`}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>

          {detailMsg && (
            <div className="text-sm text-gray-700">{detailMsg}</div>
          )}

          {/* Inline edit panel */}
          {editing && (
            <form
              onSubmit={handleUpdate}
              className="p-4 border rounded bg-white space-y-3"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-sm font-medium">Date</label>
                  <input
                    type="date"
                    value={updDate}
                    onChange={(e) => setUpdDate(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Time Zone
                  </label>
                  <select
                    value={updZone}
                    onChange={(e) => setUpdZone(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                  >
                    {ZONES.map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Start time
                  </label>
                  <input
                    type="time"
                    step={900}
                    value={updStart}
                    onChange={(e) => setUpdStart(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    End time
                  </label>
                  <input
                    type="time"
                    step={900}
                    value={updEnd}
                    onChange={(e) => setUpdEnd(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Telehealth
                  </label>
                  <select
                    value={
                      updTele === undefined ? "" : updTele ? "true" : "false"
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setUpdTele(v === "" ? undefined : v === "true");
                    }}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">(no change)</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 rounded text-white ${saving ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );

  const renderCreateTab = () => (
    <div className="max-w-2xl p-4 space-y-4">
      <form
        onSubmit={handleCreate}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {/* Type */}
        <div>
          <label className="block mb-1 font-medium">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="BLOCK">BLOCK</option>
            <option value="SINGLE">SINGLE</option>
          </select>
        </div>

        {/* Appointment Type */}
        <div>
          <label className="block mb-1 font-medium">Appointment Type</label>
          <select
            value={appointmentType}
            onChange={(e) => {
              setAppointmentType(e.target.value);
              if (e.target.value === "TELEHEALTH") setTelehealth(true);
            }}
            className="w-full border rounded px-3 py-2"
          >
            <option value="NEW_PATIENT">New Patient</option>
            <option value="FOLLOW_UP">Follow Up</option>
            <option value="TELEHEALTH">Telehealth</option>
          </select>
        </div>

        {/* Telehealth */}
        <div>
          <label className="block mb-1 font-medium">Telehealth</label>
          <select
            value={telehealth ? "yes" : "no"}
            onChange={(e) => setTelehealth(e.target.value === "yes")}
            className="w-full border rounded px-3 py-2"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="block mb-1 font-medium">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>

        {/* Zone */}
        <div>
          <label className="block mb-1 font-medium">Time Zone</label>
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>

        {/* Start time */}
        <div>
          <label className="block mb-1 font-medium">Start time</label>
          <input
            type="time"
            step={60}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>

        {/* End time */}
        <div>
          <label className="block mb-1 font-medium">End time</label>
          <input
            type="time"
            step={60}
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>

        {/* Duration (BLOCK only) */}
        {type === "BLOCK" && (
          <div>
            <label className="block mb-1 font-medium">Duration (minutes)</label>
            <input
              type="number"
              min={15}
              max={60}
              step={1}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="w-full border rounded px-3 py-2"
              placeholder="Minutes (15–60)"
              required
            />
          </div>
        )}

        {/* Submit */}
        <div className="md:col-span-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || createErrors.length > 0}
            className={`px-4 py-2 rounded text-white ${
              submitting || createErrors.length
                ? "bg-gray-400"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {submitting ? "Creating..." : "Create Availability"}
          </button>
          {createErrors.length > 0 && (
            <ul className="text-sm text-red-600 list-disc list-inside">
              {createErrors.map((e, idx) => (
                <li key={idx}>{e}</li>
              ))}
            </ul>
          )}
          {createMsg && <p className="text-sm text-green-700">{createMsg}</p>}
        </div>
      </form>
    </div>
  );

  const renderViewTab = () => {
    if (selectedZdt) return renderDetailView();

    return (
      <div className="max-w-3xl p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block mb-1 font-medium">Date</label>
            <input
              type="date"
              value={viewDate}
              onChange={(e) => setViewDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Time Zone</label>
            <select
              value={viewZone}
              onChange={(e) => setViewZone(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 font-medium">Telehealth</label>
            <select
              value={viewTelehealth}
              onChange={(e) => setViewTelehealth(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">All</option>
              <option value="true">Telehealth only</option>
              <option value="false">In-person only</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchStartTimes}
              className="w-full px-4 py-2 rounded bg-gray-800 text-white hover:bg-black"
            >
              {loadingTimes ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Clickable grid of start times */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {startTimes.map((zdt, idx) => (
            <button
              key={`${zdt}-${idx}`}
              onClick={() => handleSelectSlot(zdt)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-center"
              title={zdt}
            >
              {humanTime(zdt, viewZone)}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-4">Availabilities</h3>
      <div className="flex space-x-2 overflow-x-auto mb-4">
        {subTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2 rounded capitalize whitespace-nowrap ${
              activeTab === tab
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>
      {activeTab === "create availability"
        ? renderCreateTab()
        : renderViewTab()}
    </div>
  );
}
